import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Globe2,
  Layers,
  Map as MapIcon,
  MessageSquareQuote,
  Search,
  Tags,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SITEMAP_GROUPS, SITEMAP_TOTAL } from "../data/sitemap";
import { Reveal, Eyebrow } from "../components/common";

const PER_PAGE = 163;

const GROUP_ICONS = {
  pages: FileText,
  destinations: Globe2,
  projects: Layers,
  topics: Tags,
  reviews: MessageSquareQuote,
  blog: MapIcon,
};

/** Flatten groups, then slice into pages so each page holds 150-200 links. */
const FLAT = SITEMAP_GROUPS.flatMap((g) =>
  g.items.map((it) => ({ ...it, group: g.key, groupLabel: g.label }))
);

const PAGES = [];
for (let i = 0; i < FLAT.length; i += PER_PAGE) PAGES.push(FLAT.slice(i, i + PER_PAGE));

function groupSlices(items) {
  const out = [];
  items.forEach((it) => {
    const last = out[out.length - 1];
    if (last && last.key === it.group) last.items.push(it);
    else out.push({ key: it.group, label: it.groupLabel, items: [it] });
  });
  return out;
}

const PAGE_SUMMARY = PAGES.map((p) => {
  const labels = [...new Set(p.map((i) => i.groupLabel))];
  return labels.length > 1 ? `${labels[0]} → ${labels[labels.length - 1]}` : labels[0];
});

export default function Sitemap() {
  const { page } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const current = Math.min(Math.max(parseInt(page || "1", 10) || 1, 1), PAGES.length);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [current]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    return FLAT.filter(
      (i) => i.t.toLowerCase().includes(q) || i.p.toLowerCase().includes(q)
    ).slice(0, 400);
  }, [query]);

  const slices = groupSlices(PAGES[current - 1] || []);

  return (
    <div className="bg-white">
      <Navbar />

      {/* Head */}
      <section className="bg-ivi-mint pb-16 pt-32 lg:pt-36">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <nav className="flex items-center gap-2 text-[13px] font-semibold text-ivi-slateSoft">
            <Link to="/" className="transition-colors duration-200 hover:text-ivi-greenDark">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-ivi-greenDark">Sitemap</span>
          </nav>

          <div className="mt-6 grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-end">
            <div>
              <Eyebrow>HTML sitemap</Eyebrow>
              <h1 className="mt-5 font-heading text-[38px] font-extrabold leading-[1.08] tracking-[-0.02em] text-ivi-slate sm:text-[52px]">
                Every page on volunteering.org.au
              </h1>
              <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ivi-slateSoft">
                All <strong className="font-semibold text-ivi-slate">{SITEMAP_TOTAL.toLocaleString()}</strong>{" "}
                URLs from our XML sitemap, organised for humans across{" "}
                {PAGES.length} pages of about {PER_PAGE} links each destinations, projects,
                volunteer reviews, guides and topics.
              </p>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ivi-slateSoft" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search all 1,300 pages…"
                className="h-14 w-full rounded-full border border-ivi-mintDeep bg-white pl-12 pr-12 text-[15px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ivi-slateSoft focus:border-ivi-green focus:ring-2 focus:ring-ivi-green/25"
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  className="absolute right-4 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-ivi-mint text-ivi-greenDark transition-colors duration-200 hover:bg-ivi-green hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Group summary */}
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SITEMAP_GROUPS.map((g) => {
              const I = GROUP_ICONS[g.key] || FileText;
              return (
                <div
                  key={g.key}
                  className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_10px_26px_-22px_rgba(24,40,32,0.5)]"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ivi-mint text-ivi-greenDark">
                    <I className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-heading text-[14px] font-bold text-ivi-slate">
                      {g.label}
                    </span>
                    <span className="block text-[12px] font-semibold text-ivi-slateSoft">
                      {g.items.length} pages
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Page tabs */}
      {!results && (
        <section className="sticky top-[74px] z-30 border-b border-ivi-mint bg-white/95 py-4 backdrop-blur">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-5 lg:px-8">
            <span className="mr-2 font-heading text-[12px] font-bold uppercase tracking-[0.16em] text-ivi-slateSoft">
              Sitemap page
            </span>
            {PAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate(i === 0 ? "/sitemap" : `/sitemap/${i + 1}`)}
                title={PAGE_SUMMARY[i]}
                className={`h-10 w-10 rounded-full font-heading text-[14px] font-bold transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${
                  current === i + 1
                    ? "bg-ivi-green text-white shadow-[0_10px_20px_-12px_rgba(90,158,47,1)]"
                    : "bg-ivi-sand text-ivi-slate hover:bg-ivi-mint"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <span className="ml-auto text-[13px] font-semibold text-ivi-slateSoft">
              {(current - 1) * PER_PAGE + 1}
              {Math.min(current * PER_PAGE, SITEMAP_TOTAL)} of {SITEMAP_TOTAL.toLocaleString()}
            </span>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          {results ? (
            <>
              <h2 className="font-heading text-[24px] font-extrabold text-ivi-slate">
                {results.length} result{results.length === 1 ? "" : "s"} for &ldquo;{query}&rdquo;
              </h2>
              {results.length === 0 ? (
                <p className="mt-4 text-[16px] text-ivi-slateSoft">
                  Nothing matched. Try a country, a project type or a keyword like
                  &ldquo;turtle&rdquo;.
                </p>
              ) : (
                <LinkColumns items={results} />
              )}
            </>
          ) : (
            slices.map((s) => {
              const I = GROUP_ICONS[s.key] || FileText;
              return (
                <div key={s.key + s.items[0].p} className="mb-14 last:mb-0">
                  <div className="flex items-center gap-3 border-b border-ivi-mint pb-4">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-ivi-mint text-ivi-greenDark">
                      <I className="h-5 w-5" />
                    </span>
                    <h2 className="font-heading text-[22px] font-extrabold text-ivi-slate sm:text-[26px]">
                      {s.label}
                    </h2>
                    <span className="ml-auto rounded-full bg-ivi-sand px-3 py-1 font-heading text-[12px] font-bold text-ivi-slateSoft">
                      {s.items.length} on this page
                    </span>
                  </div>
                  <LinkColumns items={s.items} />
                </div>
              );
            })
          )}

          {/* Pagination */}
          {!results && (
            <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-ivi-mint pt-8">
              <button
                type="button"
                disabled={current === 1}
                onClick={() => navigate(current - 1 === 1 ? "/sitemap" : `/sitemap/${current - 1}`)}
                className="inline-flex items-center gap-2 rounded-full border border-ivi-mintDeep px-6 py-3 font-heading text-[14px] font-bold text-ivi-slate transition-[background-color,opacity] duration-200 hover:bg-ivi-mint disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Previous page
              </button>
              <p className="font-heading text-[14px] font-bold text-ivi-slateSoft">
                Page {current} of {PAGES.length}
              </p>
              <button
                type="button"
                disabled={current === PAGES.length}
                onClick={() => navigate(`/sitemap/${current + 1}`)}
                className="inline-flex items-center gap-2 rounded-full bg-ivi-green px-6 py-3 font-heading text-[14px] font-bold text-white transition-[background-color,opacity] duration-200 hover:bg-ivi-greenDark disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next page <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function LinkColumns({ items }) {
  return (
    <ul className="mt-6 columns-1 gap-x-10 sm:columns-2 lg:columns-3 xl:columns-4">
      {items.map((it) => (
        <li key={it.p} className="break-inside-avoid">
          <Link
            to={it.p}
            className="group flex items-start gap-2 py-[7px] text-[14px] leading-snug text-ivi-slate transition-colors duration-150 hover:text-ivi-greenDark"
          >
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-ivi-mintDeep transition-colors duration-150 group-hover:bg-ivi-green" />
            <span className="group-hover:underline">{it.t}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
